
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useUser } from '@/contexts/UserContext';
import { Presupuesto, Cliente, QuoteStatus } from '@shared/types';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { ExportDialog } from '@/components/ExportDialog';
import { Plus, Trash, Pencil, ChevronsUpDown, Download, Loader2, FileText } from 'lucide-react';
import * as XLSX from 'xlsx';

// Tipos
type EnrichedPresupuesto = Presupuesto & { clienteNombre: string };
type SortKey = keyof EnrichedPresupuesto | 'total';

// Constantes y Helpers
const PRESUPUESTO_COLUMNS = [
  { id: 'numero', name: 'Nº Presupuesto' },
  { id: 'clienteNombre', name: 'Cliente' },
  { id: 'fecha', name: 'Fecha' },
  { id: 'vencimiento', name: 'Vencimiento' },
  { id: 'total', name: 'Total' },
  { id: 'estado', name: 'Estado' },
];

const getStatusBadgeVariant = (status: QuoteStatus) => {
  const variants: Record<QuoteStatus, 'secondary' | 'destructive' | 'default'> = {
    'Aceptado': 'secondary',
    'Rechazado': 'destructive',
    'Pendiente': 'default',
  };
  return variants[status] || 'default';
};

export default function PresupuestosPage() {
  const navigate = useNavigate();
  const { fetchWithAuth } = useUser();
  const [presupuestos, setPresupuestos] = useState<Presupuesto[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>({ key: 'fecha', direction: 'desc' });
  const [isExportDialog, setExportDialog] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [presupuestosRes, clientesRes] = await Promise.all([
        fetchWithAuth('/api/presupuestos'),
        fetchWithAuth('/api/clientes'),
      ]);
      if (!presupuestosRes.ok || !clientesRes.ok) throw new Error('No se pudieron obtener los datos');
      const [presupuestosData, clientesData] = await Promise.all([presupuestosRes.json(), clientesRes.json()]);
      setPresupuestos(presupuestosData);
      setClientes(clientesData);
    } catch (error) {
      toast.error('Error al cargar datos', { description: error instanceof Error ? error.message : 'Error desconocido' });
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const enrichedPresupuestos = useMemo(() => {
    const clienteMap = new Map(clientes.map(c => [c.id, c.nombre]));
    return presupuestos.map(p => ({
      ...p,
      clienteNombre: clienteMap.get(p.clienteId) || 'Cliente Desconocido',
    }));
  }, [presupuestos, clientes]);

  const sortedAndFiltered = useMemo(() => {
    const filtered = enrichedPresupuestos.filter(p =>
        p.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.clienteNombre.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return [...filtered].sort((a, b) => {
        const valA = a[sortConfig.key] ?? '';
        const valB = b[sortConfig.key] ?? '';
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });
  }, [enrichedPresupuestos, searchTerm, sortConfig]);

  const handleSort = (key: SortKey) => {
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  };

  const handleDeleteConfirm = () => {
    if (!deleteId) return;
    const promise = fetchWithAuth(`/api/presupuestos/${deleteId}`, { method: 'DELETE' }).then(res => {
      if (!res.ok) throw new Error('No se pudo eliminar el presupuesto');
    });

    toast.promise(promise, {
      loading: 'Eliminando presupuesto...',
      success: () => {
        fetchData();
        setDeleteId(null);
        return 'Presupuesto eliminado con éxito';
      },
      error: (err) => `Error: ${err.message}`,
    });
  };

  const handleDownloadPDF = (presupuestoId: string) => {
    navigate(`/presupuestos/${presupuestoId}/edit?showPreview=true`);
  };

  const handleExport = (selectedHeaders: string[]) => {
    const dataToExport = sortedAndFiltered.map(p => {
      const row: { [key: string]: any } = {};
      selectedHeaders.forEach(header => {
        switch (header) {
          case 'Nº Presupuesto': row[header] = p.numero; break;
          case 'Cliente': row[header] = p.clienteNombre; break;
          case 'Fecha': row[header] = new Date(p.fecha).toLocaleDateString(); break;
          case 'Vencimiento': row[header] = p.vencimiento ? new Date(p.vencimiento).toLocaleDateString() : ''; break;
          case 'Total': row[header] = p.total; break;
          case 'Estado': row[header] = p.estado; break;
        }
      });
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(dataToExport, { header: selectedHeaders });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Presupuestos");
    XLSX.writeFile(wb, "presupuestos.xlsx");
    toast.success("Exportación completada");
  };

  const SortableHeader: React.FC<{ sortKey: SortKey, label: string, className?: string }> = ({ sortKey, label, className }) => (
      <TableHead onClick={() => handleSort(sortKey)} className={`cursor-pointer hover:bg-muted/50 transition-colors ${className}`}>
          <div className="flex items-center gap-2">{label} {sortConfig.key === sortKey && <ChevronsUpDown className="h-4 w-4"/>}</div>
      </TableHead>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
        <div>
            <h1 className="text-3xl font-bold tracking-tighter">Presupuestos</h1>
            <p className="text-muted-foreground mt-1">Crea y gestiona tus presupuestos para clientes.</p>
        </div>
        <div className="flex gap-2">
            <Button variant="outline" onClick={() => setExportDialog(true)} disabled={presupuestos.length === 0}>
                <Download className="mr-2 h-4 w-4" /> Exportar
            </Button>
            <Button onClick={() => navigate('/presupuestos/nuevo')}>
                <Plus className="mr-2 h-4 w-4" /> Nuevo Presupuesto
            </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
            <Input
                placeholder="Buscar por número o cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-sm"
            />
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader sortKey="numero" label="Nº" />
                  <SortableHeader sortKey="clienteNombre" label="Cliente" />
                  <SortableHeader sortKey="fecha" label="Fecha" />
                  <SortableHeader sortKey="total" label="Total" className="text-right" />
                  <SortableHeader sortKey="estado" label="Estado" />
                  <TableHead className="text-right pr-6">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                 {isLoading ? (
                    <TableRow><TableCell colSpan={6} className="h-24 text-center"><Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" /></TableCell></TableRow>
                 ) : sortedAndFiltered.length > 0 ? sortedAndFiltered.map((p) => (
                  <TableRow key={p.id} onDoubleClick={() => navigate(`/presupuestos/${p.id}/edit`)} className="hover:bg-muted/50 cursor-pointer">
                    <TableCell className="font-medium">{p.numero}</TableCell>
                    <TableCell className="text-muted-foreground">{p.clienteNombre}</TableCell>
                    <TableCell className="text-muted-foreground">{new Date(p.fecha).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right font-mono">{(p.total ?? 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</TableCell>
                    <TableCell><Badge variant={getStatusBadgeVariant(p.estado || 'Pendiente')}>{p.estado}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleDownloadPDF(p.id!)}} title="Descargar PDF">
                          <FileText className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); navigate(`/presupuestos/${p.id}/edit`)}}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setDeleteId(p.id ?? null)}}>
                          <Trash className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">No has creado ningún presupuesto todavía.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      
      <ExportDialog 
        open={isExportDialog} 
        onOpenChange={setExportDialog} 
        allHeaders={PRESUPUESTO_COLUMNS.map(c => c.name)}
        onExport={handleExport}
      />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
            <AlertDialogHeader>
                <AlertDialogTitle>¿Confirmar eliminación?</AlertDialogTitle>
                <AlertDialogDescription>Esta acción no se puede deshacer. El presupuesto será eliminado permanentemente.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteConfirm}>Eliminar</AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
    </div>
  );
}
